import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { axiosKK } from '../../../services/networkRequest';
import errorMessage from '../../../services/errorMessage';
import { OrderIssueType, StageProgress } from '../../../models/APIModels';
import {
  FilterBar,
  DataTable,
  DataTableColumn,
  ExportCsvButton,
  EmptyState,
  AdminNotice,
  SegmentedControl,
  SectionHead,
} from '../../../components/admin/Shared';
import { DateDropdown } from '../../../components/DateDropdown';

type SubTab = 'inbound' | 'outbound' | 'storage' | 'box-history';

interface InboundOrderRow {
  orderId: number;
  customer?: string;
  courier?: string;
  date?: string;
  boxes?: number;
  status?: string;
  unloadProgress?: StageProgress;
  issues?: { type: OrderIssueType }[];
}
interface InboundResponse {
  orders?: InboundOrderRow[];
  rows?: InboundOrderRow[];
}

interface OutboundOrderRow {
  orderId: number;
  customer?: string;
  courier?: string;
  date?: string;
  boxes?: number;
  status?: string;
  pickProgress?: StageProgress;
  holdoutProgress?: StageProgress;
  onsProgress?: StageProgress;
  issues?: { type: OrderIssueType }[];
}
interface OutboundResponse {
  orders?: OutboundOrderRow[];
  rows?: OutboundOrderRow[];
}

interface StorageRow {
  orderId: number;
  customer?: string;
  boxes?: number;
  months?: number;
  amount?: number;
}
interface StorageResponse {
  rows?: StorageRow[];
  orders?: StorageRow[];
}

interface BoxHistoryRow {
  bid?: string;
  orderId?: number;
  customer?: string;
  location?: string;
  lastScan?: string;
}
interface BoxHistoryResponse {
  rows?: BoxHistoryRow[];
  boxes?: BoxHistoryRow[];
  items?: BoxHistoryRow[];
}

// The tab opens on today and this month, as it did before the Phase 12 redesign.
// Opening it empty sent `date=` / `month=`, and storage-api parses whatever it is
// given -- an empty string throws, so every view answered 500 until a date was
// picked. The params below also drop an emptied field, so clearing the picker falls
// back to the API's own default (today / this month) instead of failing.
function monthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function progressLabel(p?: StageProgress): string {
  if (!p) return '—';
  return `${p.scanned ?? 0}/${p.total ?? 0}`;
}

function issuesLabel(items?: { type: OrderIssueType }[]): string {
  if (!items || items.length === 0) return '—';
  return items.map((i) => i.type).join(', ');
}

// Column emphasis: row identity (Customer) is primary, supporting context
// is secondary, system meta (boxes count, scan progress, IDs) is meta.
const INBOUND_COLUMNS: DataTableColumn<InboundOrderRow>[] = [
  {
    key: 'customer',
    label: 'Customer',
    emphasis: 'primary',
    render: (r) => r.customer || '—',
  },
  {
    key: 'courier',
    label: 'Courier',
    emphasis: 'secondary',
    render: (r) => r.courier || '—',
  },
  {
    key: 'date',
    label: 'Date',
    emphasis: 'secondary',
    render: (r) => r.date || '—',
  },
  {
    key: 'status',
    label: 'Status',
    emphasis: 'secondary',
    render: (r) => r.status || '—',
  },
  {
    key: 'unloadProgress',
    label: 'Unload',
    emphasis: 'meta',
    render: (r) => progressLabel(r.unloadProgress),
  },
  {
    key: 'boxes',
    label: 'Boxes',
    emphasis: 'meta',
    align: 'numeric',
    render: (r) => r.boxes ?? '—',
  },
  {
    key: 'issues',
    label: 'Issues',
    emphasis: 'meta',
    render: (r) => issuesLabel(r.issues),
  },
  {
    key: 'orderId',
    label: 'Order',
    emphasis: 'meta',
    align: 'numeric',
    render: (r) => `#${r.orderId}`,
  },
];

const OUTBOUND_COLUMNS: DataTableColumn<OutboundOrderRow>[] = [
  {
    key: 'customer',
    label: 'Customer',
    emphasis: 'primary',
    render: (r) => r.customer || '—',
  },
  {
    key: 'courier',
    label: 'Courier',
    emphasis: 'secondary',
    render: (r) => r.courier || '—',
  },
  {
    key: 'date',
    label: 'Date',
    emphasis: 'secondary',
    render: (r) => r.date || '—',
  },
  {
    key: 'status',
    label: 'Status',
    emphasis: 'secondary',
    render: (r) => r.status || '—',
  },
  {
    key: 'pickProgress',
    label: 'Pick',
    emphasis: 'meta',
    render: (r) => progressLabel(r.pickProgress),
  },
  {
    key: 'holdoutProgress',
    label: 'Holdout',
    emphasis: 'meta',
    render: (r) => progressLabel(r.holdoutProgress),
  },
  {
    key: 'onsProgress',
    label: 'ONS',
    emphasis: 'meta',
    render: (r) => progressLabel(r.onsProgress),
  },
  {
    key: 'boxes',
    label: 'Boxes',
    emphasis: 'meta',
    align: 'numeric',
    render: (r) => r.boxes ?? '—',
  },
  {
    key: 'issues',
    label: 'Issues',
    emphasis: 'meta',
    render: (r) => issuesLabel(r.issues),
  },
  {
    key: 'orderId',
    label: 'Order',
    emphasis: 'meta',
    align: 'numeric',
    render: (r) => `#${r.orderId}`,
  },
];

const STORAGE_COLUMNS: DataTableColumn<StorageRow>[] = [
  {
    key: 'customer',
    label: 'Customer',
    emphasis: 'primary',
    render: (r) => r.customer || '—',
  },
  {
    key: 'boxes',
    label: 'Boxes',
    emphasis: 'secondary',
    align: 'numeric',
    render: (r) => r.boxes ?? '—',
  },
  {
    key: 'months',
    label: 'Months',
    emphasis: 'secondary',
    align: 'numeric',
    render: (r) => r.months ?? '—',
  },
  {
    key: 'amount',
    label: 'Amount',
    emphasis: 'meta',
    align: 'numeric',
    render: (r) =>
      r.amount != null ? `£${(r.amount / 100).toFixed(2)}` : '—',
  },
  {
    key: 'orderId',
    label: 'Order',
    emphasis: 'meta',
    align: 'numeric',
    render: (r) => `#${r.orderId}`,
  },
];

const BOX_HISTORY_COLUMNS: DataTableColumn<BoxHistoryRow>[] = [
  {
    key: 'customer',
    label: 'Customer',
    emphasis: 'primary',
    render: (r) => r.customer || '—',
  },
  {
    key: 'location',
    label: 'Location',
    emphasis: 'secondary',
    render: (r) => r.location || '—',
  },
  {
    key: 'lastScan',
    label: 'Last scan',
    emphasis: 'secondary',
    render: (r) => r.lastScan || '—',
  },
  {
    key: 'bid',
    label: 'Box ID',
    emphasis: 'meta',
    render: (r) => r.bid || '—',
  },
  {
    key: 'orderId',
    label: 'Order',
    emphasis: 'meta',
    align: 'numeric',
    render: (r) => (r.orderId != null ? `#${r.orderId}` : '—'),
  },
];

const VIEW_OPTIONS: { id: SubTab; label: string }[] = [
  { id: 'inbound', label: 'Inbound' },
  { id: 'outbound', label: 'Outbound' },
  { id: 'storage', label: 'Storage' },
  { id: 'box-history', label: 'Box history' },
];

interface WarehouseTabProps {
  partnerId: number | null;
}

export default function WarehouseTab({ partnerId }: WarehouseTabProps) {
  const history = useHistory();
  const [tab, setTab] = useState<SubTab>('inbound');

  const [date, setDate] = useState<string>(todayISO);
  const [month, setMonth] = useState<string>(monthISO);
  const [search, setSearch] = useState('');
  const [issueFilter, setIssueFilter] = useState<OrderIssueType | ''>('');

  const [inbound, setInbound] = useState<InboundOrderRow[]>([]);
  const [outbound, setOutbound] = useState<OutboundOrderRow[]>([]);
  const [storage, setStorage] = useState<StorageRow[]>([]);
  const [history_, setHistory] = useState<BoxHistoryRow[]>([]);

  const [loading, setLoading] = useState(false);
  const [netError, setNetError] = useState<string>();

  useEffect(() => {
    if (partnerId == null) {
      setInbound([]);
      setOutbound([]);
      setStorage([]);
      setHistory([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);

    if (tab === 'inbound') {
      const params: Record<string, unknown> = { date: date || undefined, partnerId };
      if (issueFilter) params.issue = issueFilter;
      axiosKK
        .get<InboundResponse>('/logistics/dashboard/warehouse/inbound', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setInbound(data.orders || data.rows || []);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else if (tab === 'outbound') {
      const params: Record<string, unknown> = { date: date || undefined, partnerId };
      if (issueFilter) params.issue = issueFilter;
      axiosKK
        .get<OutboundResponse>('/logistics/dashboard/warehouse/outbound', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setOutbound(data.orders || data.rows || []);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else if (tab === 'storage') {
      axiosKK
        .get<StorageResponse>('/logistics/dashboard/warehouse/storage', {
          params: { month: month || undefined, partnerId },
        })
        .then(({ data }) => {
          if (cancelled) return;
          setStorage(data.rows || data.orders || []);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    } else {
      const params: Record<string, unknown> = { month: month || undefined, partnerId };
      if (search) params.search = search;
      axiosKK
        .get<BoxHistoryResponse>('/logistics/dashboard/warehouse/box-history', { params })
        .then(({ data }) => {
          if (cancelled) return;
          setHistory(data.rows || data.boxes || data.items || []);
        })
        .catch((err) => { if (!cancelled) errorMessage(setNetError)(err); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }

    return () => {
      cancelled = true;
    };
  }, [tab, date, month, search, issueFilter, partnerId]);

  const exportConfig = (() => {
    if (tab === 'inbound') {
      return {
        endpoint: '/logistics/dashboard/warehouse/inbound',
        filename: `warehouse-inbound-${date}.csv`,
        params: {
          date,
          partnerId: partnerId ?? undefined,
          issue: issueFilter || undefined,
        },
      };
    }
    if (tab === 'outbound') {
      return {
        endpoint: '/logistics/dashboard/warehouse/outbound',
        filename: `warehouse-outbound-${date}.csv`,
        params: {
          date,
          partnerId: partnerId ?? undefined,
          issue: issueFilter || undefined,
        },
      };
    }
    if (tab === 'storage') {
      return {
        endpoint: '/logistics/dashboard/warehouse/storage',
        filename: `warehouse-storage-${month}.csv`,
        params: { month, partnerId: partnerId ?? undefined },
      };
    }
    return {
      endpoint: '/logistics/dashboard/warehouse/box-history',
      filename: `warehouse-box-history-${month}.csv`,
      params: {
        month,
        partnerId: partnerId ?? undefined,
        search: search || undefined,
      },
    };
  })();

  const openOrder = (orderId?: number) => {
    if (orderId == null) return;
    history.push(`/admin/order/${orderId}`);
  };

  return (
    <>
      <SectionHead
        title="Warehouse dashboard"
        caption="Track inbound, outbound, storage and box history for the selected partner."
        infoKey="logistics.warehouse"
      />

      <AdminNotice intent="error">{netError}</AdminNotice>

      <section className="admin-warehouse-forecast">
        <FilterBar
          actions={
            <ExportCsvButton
              endpoint={exportConfig.endpoint}
              filename={exportConfig.filename}
              params={exportConfig.params}
              disabled={partnerId == null}
            />
          }
        >
          <FilterBar.Item label="View">
            <SegmentedControl<SubTab>
              items={VIEW_OPTIONS}
              activeId={tab}
              onChange={setTab}
              ariaLabel="Warehouse views"
            />
          </FilterBar.Item>

          {(tab === 'inbound' || tab === 'outbound') && (
            <>
              <FilterBar.Item label="Date" htmlFor="warehouse-date">
                <DateDropdown
                  id="warehouse-date"
                  value={date || null}
                  onChange={(iso) => setDate(iso ?? '')}
                  label="Date"
                  placeholder="Select date"
                />
              </FilterBar.Item>
              <FilterBar.Item label="Issue" htmlFor="warehouse-issue">
                <select
                  id="warehouse-issue"
                  value={issueFilter}
                  onChange={(e) => setIssueFilter(e.target.value as OrderIssueType | '')}
                >
                  <option value="">All</option>
                  <option value="damaged">Damaged</option>
                  <option value="overweight">Overweight</option>
                  <option value="repack">Repack</option>
                  <option value="other">Other</option>
                </select>
              </FilterBar.Item>
            </>
          )}
          {(tab === 'storage' || tab === 'box-history') && (
            <FilterBar.Item label="Month" htmlFor="warehouse-month">
              <input
                id="warehouse-month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              />
            </FilterBar.Item>
          )}
          {tab === 'box-history' && (
            <FilterBar.Item label="Search" htmlFor="warehouse-search">
              <input
                id="warehouse-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="BID or order id"
              />
            </FilterBar.Item>
          )}
        </FilterBar>

        {partnerId == null ? (
          <EmptyState
            variant="select-prerequisite"
            title="Select a partner"
            body="Pick a partner from the picker above to load the warehouse dashboard."
          />
        ) : tab === 'inbound' ? (
          <DataTable<InboundOrderRow>
            columns={INBOUND_COLUMNS}
            rows={inbound}
            keyOf={(r, i) => r.orderId ?? i}
            loading={loading}
            emptyMessage="No inbound orders."
            onRowClick={(r) => openOrder(r.orderId)}
          />
        ) : tab === 'outbound' ? (
          <DataTable<OutboundOrderRow>
            columns={OUTBOUND_COLUMNS}
            rows={outbound}
            keyOf={(r, i) => r.orderId ?? i}
            loading={loading}
            emptyMessage="No outbound orders."
            onRowClick={(r) => openOrder(r.orderId)}
          />
        ) : tab === 'storage' ? (
          <DataTable<StorageRow>
            columns={STORAGE_COLUMNS}
            rows={storage}
            keyOf={(r, i) => r.orderId ?? i}
            loading={loading}
            emptyMessage="No storage rows."
            onRowClick={(r) => openOrder(r.orderId)}
          />
        ) : (
          <DataTable<BoxHistoryRow>
            columns={BOX_HISTORY_COLUMNS}
            rows={history_}
            keyOf={(r, i) => r.bid ?? i}
            loading={loading}
            emptyMessage="No box history."
            onRowClick={(r) => openOrder(r.orderId)}
          />
        )}
      </section>
    </>
  );
}
